import { AsyncPipe, DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture, GroupItemType } from '@cds/betting-offer';
import { Region, Sport } from '@cds/betting-offer/tags';
import { CDNCachingConfig, ComposableConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { GridGrouping } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { Widget, WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { TabbedGridLink, TabbedGridLinkTarget, TabbedGridPayload } from '@frontend/sports/types/components/widget/types';
import { DeviceService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { ItemComponent } from '../../common/item/item.component';
import { GridModule } from '../../grid/grid.module';
import { TabbedGridTabBarItem } from '../../tabbed-grid/tabbed-grid-navigation.service';
import { WidgetContextService } from '../common/widget-context.service';
import { WidgetTabBarDisplay } from '../common/widget-tab-bar.component';

export interface AccordionData extends TabbedGridTabBarItem {
    grid?: ObservableGrid;
    linkConfig?: AccordionLinkData;
    isCollapsed: boolean;
    eventCount?: number;
    isShowMoreExpanded: boolean;
    hideMarketSwitcher: boolean;
    trackingData?: { [key: string]: string };
}

export interface AccordionLinkData {
    link: string;
    count?: number;
    title: string;
}

const COMPOSABLE_ACCORDION = 'ComposableAccordion';

@Component({
    selector: 'ms-composable-accordions',
    templateUrl: './composable-accordions.component.html',
    styleUrl: './composable-accordions.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, AsyncPipe, GridModule, CompetitionLogoComponent, ItemComponent, TrustPipe, IconCustomComponent],
})
export class ComposableAccordionsComponent implements OnChanges {
    @HostBinding('class') className = 'grid-wrapper composable-accordions';

    @Input() navigation: TabbedGridTabBarItem[];
    @Input() widgets: Widget<unknown>[];
    @Input() isShowMoreEnabled: boolean;
    @Input() maxEventsPerAccordion: number;

    @Output() trackAccordionClick = new EventEmitter<{
        widget: Widget<unknown>;
        widgetContext: Partial<WidgetContext>;
        itemId: string;
    }>();
    @Output() trackLinkClick = new EventEmitter<{
        widgetId: string;
        widgetContext: Partial<WidgetContext>;
        itemId: string;
        details: string;
        trackingData?: { [key: string]: string };
        url?: string;
    }>();

    displayType = WidgetTabBarDisplay;
    accordions: AccordionData[];
    private isCDNCachingEnabled = false;

    private readonly destroyRef = inject(DestroyRef);

    private readonly _doc = inject(DOCUMENT);

    readonly isEpcotEnabled = this.epcotConfig.isEnabled;

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private deviceService: DeviceService,
        public composableConfig: ComposableConfig,
        private gridFactory: ObservableGridFactory,
        private changeDetectorRef: ChangeDetectorRef,
        private contextService: WidgetContextService,
        private cdnCachingConfig: CDNCachingConfig,
        private epcotConfig: EpcotConfig,
    ) {
        this.isCDNCachingEnabled = this.cdnCachingConfig.widgets.includes(WidgetType.Composable);
    }

    ngOnChanges(changes: ISimpleChanges<ComposableAccordionsComponent>): void {
        if (!changes.widgets) {
            return;
        }
        this.setAccordions();
    }

    private setAccordions() {
        this.accordions = this.navigation.map((item) => this.mapItem(item));
        const lastExpandedAccordion = this.accordions.filter((item) => !item.isCollapsed).pop();
        if (lastExpandedAccordion?.eventCount) {
            lastExpandedAccordion.isCollapsed = lastExpandedAccordion.eventCount < this.composableConfig.thresholdToExpandAccordion;
        }
    }

    private mapItem(item: TabbedGridTabBarItem): AccordionData {
        let linkConfig;
        const { grid, firstFixture, bottomLink, eventCount, isCollapsed, hideMarketSwitcher, trackingData } = this.getPartialData(item);

        if (bottomLink?.visible && firstFixture) {
            linkConfig = this.setLink(item, firstFixture, bottomLink);
        }

        return {
            grid,
            eventCount,
            linkConfig,
            id: item.id,
            icon: item.icon,
            group: item.group,
            title: item.title,
            isCollapsed,
            itemId: item.itemId,
            context: item.context,
            display: item.display,
            sportId: item.sportId,
            widgetId: item.widgetId,
            isShowMoreExpanded: false,
            hideMarketSwitcher,
            trackingData,
        };
    }

    private getPartialData(item: TabbedGridTabBarItem): {
        grid: ObservableGrid | undefined;
        firstFixture: Fixture | undefined;
        bottomLink: TabbedGridLink | undefined;
        eventCount: number;
        isCollapsed: boolean;
        hideMarketSwitcher: boolean;
        trackingData: { [key: string]: string } | undefined;
    } {
        const { group, widgetId, itemId } = item;
        const activeWidget = this.widgets.find((widget) => widget.id === widgetId);
        let grid;
        let bottomLink;
        let firstFixture;
        let eventCount = 0;
        let isCollapsed = true;
        let hideMarketSwitcher = false;
        let trackingData;

        if (activeWidget) {
            const payload = activeWidget.payload as TabbedGridPayload;
            trackingData = activeWidget.trackingData;
            const fixtures = payload.fixtures;
            let filteredFixtures: Fixture[] = [];

            bottomLink = payload.bottomLink;
            hideMarketSwitcher = payload.hideMarketSwitcher;

            if (group?.type === GroupItemType.Sport) {
                filteredFixtures = fixtures.filter((fixture) => fixture.sport.id === itemId);
            } else if (group?.type === GroupItemType.Competition) {
                filteredFixtures = fixtures.filter((fixture) => fixture.competition.parentLeagueId === itemId || fixture.competition.id === itemId);
            }

            if (filteredFixtures.length) {
                isCollapsed = false;
                firstFixture = filteredFixtures[0];
                grid = this.gridFactory.fromResponse(`${COMPOSABLE_ACCORDION}/${widgetId}/${itemId}`, filteredFixtures, {
                    grouping: GridGrouping.None,
                    disableGroupSorting: payload.disabledFixtureSorting,
                });
                eventCount = filteredFixtures.length;
            }
            if (!this.deviceService.isMobile || (this.deviceService.isTablet && this.deviceService.currentOrientation === 'landscape')) {
                hideMarketSwitcher = false;
            }
        }

        return { grid, firstFixture, bottomLink, eventCount, isCollapsed, hideMarketSwitcher, trackingData };
    }

    private setLink(item: TabbedGridTabBarItem, firstFixture: Fixture, bottomLink: TabbedGridLink): AccordionLinkData {
        const param =
            item.group && item.group.type !== GroupItemType.FixtureState
                ? { id: item.itemId, name: item.group.name! }
                : { id: item.itemId, name: item.itemId.toString() };

        return this.setLinkAndTitle(param, firstFixture, item, bottomLink);
    }

    private setLinkAndTitle(param: UrlParam, firstFixture: Fixture, item: TabbedGridTabBarItem, linkConfig: TabbedGridLink): AccordionLinkData {
        const linkData = <AccordionLinkData>{};
        switch (linkConfig.target) {
            case TabbedGridLinkTarget.SportLive:
                linkData.link = this.urlHelper.getLiveSportUrl({ sport: param, isEsport: firstFixture.sport.isEsport });
                linkData.count = item?.group?.count ?? 0;
                break;
            case TabbedGridLinkTarget.SportLobby:
                linkData.link = this.urlHelper.getSportUrl(param);
                linkData.count = item?.group?.count ?? 0;
                break;
            case TabbedGridLinkTarget.CompetitionLobby:
                linkData.link = this.getCompetitonData(firstFixture) ?? '';
                break;
            default:
                linkData.link = linkConfig.linkOverride ?? '';
                break;
        }

        linkData.title = interpolateSingleBracesString(
            linkConfig.title,
            {
                sport: param.name,
                competition: param.name,
            },
            false,
        );

        return linkData;
    }

    private getCompetitonData(firstFixture: Fixture): string {
        const competitionId = firstFixture.competition.parentLeagueId ?? firstFixture.competition.id;

        return this.getCompetitionURL(competitionId.toString(), firstFixture.competition.name.value, firstFixture.sport, firstFixture.region);
    }

    private getCompetitionURL(competitionId: string, competitionName: string, sport: Sport, region: Region): string {
        const sportDetail = {
            id: sport.id,
            name: sport.name.value,
        };
        const regionDetail = {
            id: region.id,
            name: region.name.value,
        };
        let competitionDetail: UrlParam | undefined;
        if (competitionId) {
            competitionDetail = {
                id: competitionId,
                name: competitionName,
            };
        }
        const league: UrlParam = {
            id: competitionId,
            name: competitionName,
        };
        const regionOrCompetition = regionDetail ? regionDetail : competitionDetail;

        return this.urlHelper.getSportUrl(sportDetail, SportUrlParam.Betting, regionOrCompetition, league, undefined);
    }

    private getAccordionData(accordion: AccordionData, index: number, numberOfEventsOnShowMoreClick: number, eventsCount?: number) {
        const { isShowMoreExpanded, context, widgetId, itemId, grid } = accordion;
        this.contextService
            .getWidget(
                {
                    ...context,
                    widgetId,
                    take: numberOfEventsOnShowMoreClick,
                    skip: eventsCount,
                    shouldIncludePayload: true,
                },
                false,
                !this.isCDNCachingEnabled,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                const { fixtures, disabledFixtureSorting } = response.payload as TabbedGridPayload;
                const trackingData = response.trackingData;

                if (isShowMoreExpanded && grid) {
                    grid.addEvents(fixtures);
                } else {
                    accordion.grid = this.gridFactory.fromResponse(`${COMPOSABLE_ACCORDION}/${widgetId}/${itemId}`, fixtures, {
                        grouping: GridGrouping.None,
                        disableGroupSorting: disabledFixtureSorting,
                    });

                    setTimeout(() => {
                        const gridElement = this._doc.getElementById(accordion.id + index) as HTMLElement;
                        gridElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                }
                const details = accordion.isShowMoreExpanded ? 'see more' : 'see less';
                this.trackLinkClick.emit({
                    widgetId: accordion.widgetId,
                    widgetContext: accordion.context,
                    itemId: accordion?.itemId.toString(),
                    details,
                    trackingData,
                });
            });
    }

    onAccordionClick(accordion: AccordionData) {
        const { widgetId, context, itemId } = accordion;
        this.contextService
            .getWidget(
                {
                    ...context,
                    widgetId,
                    take: this.maxEventsPerAccordion,
                    shouldIncludePayload: true,
                },
                false,
                !this.isCDNCachingEnabled,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((widget) => {
                const { fixtures, bottomLink, disabledFixtureSorting } = widget.payload as TabbedGridPayload;
                const selectedAccordion = this.accordions.find((a) => a.id === accordion.id);
                let linkConfig;
                if (selectedAccordion) {
                    const isCollapsed = selectedAccordion.isCollapsed;
                    selectedAccordion.isCollapsed = isCollapsed ? !isCollapsed : !!isCollapsed;
                    accordion.isShowMoreExpanded = selectedAccordion.isCollapsed;
                    if (bottomLink?.visible && fixtures?.[0]) {
                        linkConfig = this.setLink(selectedAccordion, fixtures?.[0], bottomLink);
                    }
                    selectedAccordion.linkConfig = linkConfig;
                    selectedAccordion.grid = this.gridFactory.fromResponse(`${COMPOSABLE_ACCORDION}/${widgetId}/${itemId}`, fixtures, {
                        grouping: GridGrouping.None,
                        disableGroupSorting: disabledFixtureSorting,
                    });
                    selectedAccordion.eventCount = fixtures?.length;
                    this.changeDetectorRef.markForCheck();
                    this.trackAccordionClick.emit({ widget, widgetContext: context, itemId: itemId?.toString() });
                }
            });
    }

    toggleAccordion(accordion: AccordionData, collapsed: boolean) {
        accordion.isCollapsed = collapsed;
    }

    onShowMoreClick(accordion: AccordionData, index: number) {
        accordion.isShowMoreExpanded = !accordion.isShowMoreExpanded;
        const eventsCount = accordion.grid?.getState().groups[0].count;
        const numberOfEventsOnShowMoreClick = this.composableConfig.numberOfEventsOnShowMoreClick;
        if (accordion.isShowMoreExpanded) {
            this.getAccordionData(accordion, index, numberOfEventsOnShowMoreClick, eventsCount);
        } else {
            accordion.grid?.destroy();
            this.getAccordionData(accordion, index, this.maxEventsPerAccordion);
        }
    }

    linkClick(accordion: AccordionData) {
        this.trackLinkClick.emit({
            widgetId: accordion.widgetId,
            widgetContext: accordion.context,
            itemId: accordion?.itemId.toString(),
            details: 'global link',
            trackingData: accordion.trackingData,
            url: accordion.linkConfig?.link,
        });
    }

    getEventsAvilable(accordion: AccordionData): boolean {
        const groupCount = accordion.group?.count ?? 0;

        return groupCount - accordion.eventCount! > 0;
    }
}
